// Secondary Nav for utility links and handling
if (!customElements.get('secondary-nav')) {
    class SecondaryNav extends HTMLElement {
        constructor() {
            super();
            this.ctaButtonListItem = null;
            this.originalMenu = null;
            this.targetMenu = null;
            this.isInMobileMenu = false;
            this.submenuListenersAdded = false;
        }

        connectedCallback() {
            this.init();
        }

        /**
         * Initialize secondary nav component and listeners
         */
        init() {
            this.setupCTAMovement();
            this.handleResize();
            window.addEventListener('resize', this.handleResize.bind(this));
        }

        /**
         * Handles logic for moving the CTA button dynamically
         */
        setupCTAMovement() {
            this.ctaButtonListItem = this.querySelector('.secondary-nav__cta')?.parentElement;
            this.originalMenu = this.querySelector('.secondary-nav');
            this.targetMenu = document.querySelector('.main-menu__content');

            if (!this.ctaButtonListItem || !this.originalMenu || !this.targetMenu) {
                return;
            }

            this.handleCTAMovement();
        }

        /**
         * Swaps between mobile and desktop placement
         */
        handleCTAMovement() {
            const isMobile = window.innerWidth < 820;

            if (isMobile && !this.isInMobileMenu) {
                this.moveCTAToMobile();
            } else if (!isMobile && this.isInMobileMenu) {
                this.moveCTAToDesktop();
            }
        }

        /**
         * Announcements.js places links beneath content. We need to do the same
         */
        moveCTAToMobile() {
            const observer = new MutationObserver(() => {
                const announcementLinks = this.targetMenu.querySelector('.mob__announcement-links');
                if (announcementLinks) {
                    announcementLinks.insertAdjacentElement('afterend', this.ctaButtonListItem);
                    this.isInMobileMenu = true;
                    observer.disconnect();
                }
            });

            observer.observe(this.targetMenu, { childList: true, subtree: true });

            // The announcements.js links have a weird timing on resizing.
            // we want to wait for them, or at least force ours still at the end
            setTimeout(() => {
                const announcementLinks = this.targetMenu.querySelector('.mob__announcement-links');
                if (!announcementLinks) {
                    this.targetMenu.appendChild(this.ctaButtonListItem);
                } else {
                    announcementLinks.insertAdjacentElement('afterend', this.ctaButtonListItem);
                }
                this.isInMobileMenu = true;
                observer.disconnect();
            }, 30);
        }

        /**
         * Move banner back to original position if needed
         */
        moveCTAToDesktop() {
            if (!this.originalMenu.contains(this.ctaButtonListItem)) {
                this.originalMenu.appendChild(this.ctaButtonListItem);
                this.isInMobileMenu = false;
            }
        }

        /**
         * Handles logic on window resize to add/remove listeners dynamically
         */
        handleResize() {
            // The theme nav breaks from 'mobile (hamburger)' to 'desktop (header)' at 820
            const isMobile = window.innerWidth < 820;

            if (isMobile && !this.submenuListenersAdded) {
                this.addSubmenuToggleListeners();
                this.addBackButtonListeners();
                this.preventSubmenuLinkBubbling();
                this.handleCTAMovement();
                this.submenuListenersAdded = true;
            } else if (!isMobile && this.submenuListenersAdded) {
                this.removeSubmenuListeners();
                this.resetSubmenusForDesktop();
                this.handleCTAMovement();
                this.submenuListenersAdded = false;
            }
        }

        /**
         * Adds click event listener to submenu toggle links
         */
        addSubmenuToggleListeners() {
            this.addEventListener('click', this.handleSubmenuToggleEvent);
        }

        /**
         * Removes click event listener from submenu toggle links
         */
        removeSubmenuListeners() {
            this.removeEventListener('click', this.handleSubmenuToggleEvent);
            this.removeBackButtonListeners();
            this.removeSubmenuLinkBubbling();
        }

        /**
         * Resets submenu styles for desktop hover functionality
         */
        resetSubmenusForDesktop() {
            const submenus = this.querySelectorAll('.submenu');

            submenus.forEach(submenu => {
                submenu.style.display = '';
                submenu.style.transform = '';
                submenu.setAttribute('data-visible', 'false');

                const toggle = submenu.previousElementSibling;
                if (toggle && toggle.tagName === 'A') {
                    toggle.setAttribute('aria-expanded', 'false');
                }
            });
        }

        /**
         * Handles submenu toggle click event
         * @param e
         */
        handleSubmenuToggleEvent = (e) => {
            const toggle = e.target.closest('.has-submenu > .secondary-nav__item');
            if (toggle) {
                e.preventDefault();
                e.stopPropagation();
                this.handleSubmenuToggle(e, toggle);
            }
        };

        /**
         * Handles general toggling of visibility of submenu
         * @param e
         * @param toggle
         */
        handleSubmenuToggle(e, toggle) {
            const submenu = toggle.parentNode.querySelector('.submenu');
            const isVisible = submenu.getAttribute('data-visible') === 'true';

            if (isVisible) {
                this.hideSubmenu(submenu, toggle);
            } else {
                this.showSubmenu(submenu, toggle);
            }
        }

        /**
         * Toggles submenu display and updates aria-expanded
         * @param submenu
         * @param toggle
         */
        showSubmenu(submenu, toggle) {
            submenu.style.display = 'block';
            setTimeout(() => {
                submenu.style.transform = 'translateX(0%)';
            }, 10);
            submenu.setAttribute('data-visible', 'true');
            toggle.setAttribute('aria-expanded', 'true');
        }

        /**
         * Hides submenu and updates aria-expanded
         * @param submenu
         * @param toggle
         */
        hideSubmenu(submenu, toggle) {
            submenu.style.transform = 'translateX(100%)';
            submenu.setAttribute('data-visible', 'false');
            setTimeout(() => {
                submenu.style.display = 'none';
            }, 300);
            toggle.setAttribute('aria-expanded', 'false');
        }

        /**
         * Adds click event listeners to back buttons within submenus
         */
        addBackButtonListeners() {
            const backButtons = this.querySelectorAll('.back-button');

            backButtons.forEach(button => {
                button.addEventListener('click', this.handleBackButtonClick);
            });
        }

        /**
         * Removes click event listeners from back buttons within submenus
         */
        removeBackButtonListeners() {
            const backButtons = this.querySelectorAll('.back-button');

            backButtons.forEach(button => {
                button.removeEventListener('click', this.handleBackButtonClick);
            });
        }

        /**
         * Handles back button click event
         * @param e
         */
        handleBackButtonClick = (e) => {
            e.preventDefault();

            const submenu = e.target.closest('.submenu');
            const toggle = submenu.previousElementSibling;
            if (submenu) {
                this.hideSubmenu(submenu, toggle);
            }
        };

        /**
         * Handles submenu link click event to stop bubbling
         */
        handleSubmenuLinkClick = (e) => {
            e.stopPropagation();
        };

        /**
         * Ensures submenu links perform their default navigation without toggling the parent submenu
         */
        preventSubmenuLinkBubbling() {
            const submenuLinks = this.querySelectorAll('.submenu a');

            submenuLinks.forEach(link => {
                link.addEventListener('click', this.handleSubmenuLinkClick);
            });
        }

        /**
         * Removes bubbling prevention listeners from submenu links
         */
        removeSubmenuLinkBubbling() {
            const submenuLinks = this.querySelectorAll('.submenu a');

            submenuLinks.forEach(link => {
                link.removeEventListener('click', this.handleSubmenuLinkClick);
            });
        }
    }

    customElements.define('secondary-nav', SecondaryNav);
}